import React, { useState } from "react";
import { Button, OrderStatus, getOrderStatusDisplayLabel } from "./ui";
import { MDBIcon, MDBTooltip } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { api } from "../services";
import { toast } from "react-toastify";
import RightDrawer from "./RightDrawer";
import DeleteModal from "./Modals/DeleteModal";
import OrderDetailsTab from "./OrderDetailsDrawer/OrderDetailsTab";
import CustomerInfoTab from "./OrderDetailsDrawer/CustomerInfoTab";
import OrderTimeline from "./OrderTimeline";
import "./order-drawer.css";
import {
  getOrderLineProductName,
  getModifierLineDisplayLabel,
  escapeHtmlText,
} from "../utils/orderLineLabels";

const DRAWER_TABS = [
  { id: "details", labelKey: "orderdetails" },
  { id: "timeline", labelKey: "timeline", loads: true },
  { id: "customer", labelKey: "More" },
];

/**
 * OrderDetailsDrawer - Unified tabbed drawer for complete order management
 */
const OrderDetailsDrawer = ({
  show,
  onClose,
  orderDetails,
  onOrderUpdate,
  formatDateTime,
  getExternalOrderId,
  defaultTab = "details",
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const renderOrderStatus = () => {
    const statusLabel = getOrderStatusDisplayLabel(orderDetails, t);
    const badge = (
      <OrderStatus status={orderDetails.status} uppercase={false}>
        {statusLabel}
      </OrderStatus>
    );

    if (orderDetails.status === "PROCESSED") {
      return (
        <MDBTooltip
          tag="span"
          wrapperProps={{ style: { display: "inline-flex" } }}
          title={t(
            "ordersProcessedFromPortal",
            "Orders processed from Portal or another service"
          )}
          placement="top"
        >
          {badge}
        </MDBTooltip>
      );
    }

    return badge;
  };

  const handleTabChange = (tabId, loads = false) => {
    setIsTabLoading(loads);
    setActiveTab(tabId);
  };

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isTabLoading, setIsTabLoading] = useState(false);

  React.useEffect(() => {
    if (show) {
      setActiveTab(defaultTab);
    }
  }, [show, defaultTab]);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCloseDeleteModal = () => {
    if (!isProcessing) {
      setShowDeleteModal(false);
      setOrderToDelete(null);
    }
  };

  const handleAction = async (appId, orderId, action) => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.patch(
        `/orders/${orderId}/status`,
        { status: action, appId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (onOrderUpdate) { onOrderUpdate(); }
      return response;
    } catch (error) {
      console.error("Error updating order status:", error.response?.data || error.message);
      throw error;
    }
  };

  const handleAccept = async () => {
    if (!orderDetails || isProcessing) return;
    setIsProcessing(true);
    try {
      await handleAction(orderDetails.appId._id, orderDetails._id, "ACCEPTED");
      toast.success(
        t("Order #{{id}} accepted successfully!", {
          id: getExternalOrderId(orderDetails),
        })
      );
      onClose();
    } catch (error) {
      const apiData = error.response?.data || {};
      const detail =
        (String(i18n.language || "").startsWith("ar") && apiData.message_ar) ||
        apiData.message ||
        null;
      toast.error(
        detail ||
          t("Failed to accept order #{{id}}", {
            id: getExternalOrderId(orderDetails),
          })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (!orderDetails || isProcessing) return;
    setOrderToDelete({ ...orderDetails, actionType: "CANCELLED" });
    setShowDeleteModal(true);
  };

  const handleReady = async () => {
    if (!orderDetails || isProcessing) return;
    setIsProcessing(true);
    try {
      await handleAction(orderDetails.appId._id, orderDetails._id, "READY FOR DELIVERY");
      toast.success(
        t("Order #{{id}} marked as ready for delivery!", {
          id: getExternalOrderId(orderDetails),
        })
      );
      onClose();
    } catch (error) {
      toast.error(
        t("Failed to update order #{{id}}", {
          id: getExternalOrderId(orderDetails),
        })
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!orderToDelete || isProcessing) return;
    setIsProcessing(true);
    try {
      const action = orderToDelete.actionType === "CANCELLED" ? "CANCELLED" : "REJECTED";
      await handleAction(orderToDelete.appId._id, orderToDelete._id, action);
      const successKey =
        orderToDelete.actionType === "CANCELLED"
          ? "Order #{{id}} cancelled successfully!"
          : "Order #{{id}} rejected successfully!";
      toast.success(
        t(successKey, { id: getExternalOrderId(orderToDelete) })
      );
      if (orderToDelete.actionType === "CANCELLED") { onClose(); }
    } catch (error) {
      console.error("Error in confirmDelete:", error);
      toast.error(
        t("Failed to update order #{{id}}", {
          id: getExternalOrderId(orderToDelete),
        })
      );
    } finally {
      setIsProcessing(false);
      handleCloseDeleteModal();
    }
  };

  const handlePrint = async () => {
    if (!orderDetails) return;
    try {
      const token = localStorage.getItem("token");
      const response = await api.get("/printer-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const printerConfigs = response.data.data || [];
      const branchId = orderDetails.branch || orderDetails.branchId;
      const config = printerConfigs.find(
        (cfg) => cfg.branchId === branchId || cfg.branchId === (branchId && branchId.toString())
      );
      if (!config) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write("<h2>No printer configuration found for this branch.</h2>");
        printWindow.document.close();
        return;
      }
      const numberOfCopies = config.numberOfCopies || 1;
      const paperStyle = config.paperSize === "A4" ? "width: 210mm;" : config.paperSize === "A5" ? "width: 148mm;" : "width: 105mm;";
      const logoHtml = config.logo ? `<img src="${config.logo}" alt="Logo" style="max-width:80px;max-height:80px;display:block;margin:0 auto 8px auto;" />` : "";
      const headerHtml = config.receipt_header ? `<div style="text-align:center;font-weight:bold;margin-bottom:8px;">${config.receipt_header}</div>` : "";
      const footerHtml = config.receipt_footer ? `<div style="text-align:center;font-size:12px;margin-top:12px;">${config.receipt_footer}</div>` : "";
      const currency = config.currency || "SAR";
      const fmt = (n) => (Number(n) || 0).toFixed(2);
      const isKeetaPrint = String(orderDetails.platform || "").toLowerCase() === "keeta";
      const isJahezPrint = String(orderDetails.platform || "").toUpperCase().includes("JAHEZ");
      const modifierOptPrice = (opt) => {
        if (isJahezPrint) return Number(opt.original_price ?? opt.final_price) || 0;
        return Number(opt.final_price) || 0;
      };

      const orderRows = orderDetails.products
        .map((product) => {
          const pName = escapeHtmlText(getOrderLineProductName(product, isRTL));
          const mainRow = `<tr><td>${product.quantity} x ${pName}</td><td style="text-align:right;">${fmt(product.final_price)} ${currency}</td></tr>`;
          const modRows =
            product.modifiers && product.modifiers.length > 0
              ? product.modifiers
                  .map((mod) =>
                    (mod.options || [])
                      .map((opt) => {
                        const label = escapeHtmlText(getModifierLineDisplayLabel(mod, opt, isRTL));
                        return `<tr><td style='padding-left:16px;'>+ ${opt.quantity} x ${label}</td><td style='text-align:right;'>${fmt(modifierOptPrice(opt))} ${currency}</td></tr>`;
                      })
                      .join("")
                  )
                  .join("")
              : "";
          return mainRow + modRows;
        })
        .join("");

      const lineItemsSubtotal = (orderDetails.products || []).reduce(
        (sum, p) => sum + (Number(p.final_price) || 0),
        0
      );

      const feeRows = [];
      if (orderDetails.customer_fees?.delivery_fee != null) {
        feeRows.push(`<tr><td>Delivery Fee</td><td style="text-align:right;">${fmt(orderDetails.customer_fees.delivery_fee)} ${currency}</td></tr>`);
      }
      if (isKeetaPrint) {
        feeRows.push(`<tr><td>${escapeHtmlText(t("platformFees", "Platform fees"))}</td><td style="text-align:right;">${fmt(orderDetails.merchant_fees?.platform_service_fee)} ${currency}</td></tr>`);
      }
      if (orderDetails.customer_fees?.tips != null) {
        feeRows.push(`<tr><td>Tips</td><td style="text-align:right;">${fmt(orderDetails.customer_fees.tips)} ${currency}</td></tr>`);
      }
      if (orderDetails.customer_fees?.top_up_amount != null) {
        feeRows.push(`<tr><td>Top-up Amount</td><td style="text-align:right;">${fmt(orderDetails.customer_fees.top_up_amount)} ${currency}</td></tr>`);
      }
      const feesHtml = feeRows.join("");

      const subtotalHtml = `<tr><td>Subtotal</td><td style="text-align:right;">${fmt(lineItemsSubtotal)} ${currency}</td></tr>`;
      const discountHtml = orderDetails.offer
        ? `<tr><td>Discount</td><td style="text-align:right;">- ${fmt(orderDetails.offer.amount)} ${currency}</td></tr>`
        : "";
      const totalHtml = `<tr style='font-weight:bold;'><td>Total</td><td style='text-align:right;'>${fmt(orderDetails.final_price)} ${currency}</td></tr>`;
      const branchName = config.branchName || orderDetails.branchName || "";
      const storeName = config.storeName || "";
      const html = `<html><head><title>Print Order</title><style>body { font-family: Arial, sans-serif; background: #fff; } .receipt { ${paperStyle} margin: 0 auto; padding: 16px; border: 1px solid #eee; } .receipt h2 { text-align: center; margin: 0 0 8px 0; } .receipt table { width: 100%; border-collapse: collapse; margin-top: 8px; } .receipt td { padding: 4px 0; font-size: 14px; } .receipt .meta { font-size: 13px; margin-bottom: 8px; } .receipt .footer { margin-top: 16px; font-size: 12px; text-align: center; color: #888; }</style></head><body onload="window.print()"><div class="receipt">${logoHtml}<h2>${storeName}</h2><div class="meta">Branch: ${branchName}<br>Order #: ${orderDetails.order_id}<br>Date: ${new Date(orderDetails.createdAt).toLocaleString()}</div>${headerHtml}<table>${orderRows}${subtotalHtml}${feesHtml}${discountHtml}${totalHtml}</table>${footerHtml}<div class="footer">Printer: ${config.printerType}, Paper: ${config.paperSize}</div></div></body></html>`;
      for (let i = 0; i < numberOfCopies; i++) {
        const printWindow = window.open("", "_blank");
        if (!printWindow) continue;
        printWindow.document.write(html);
        printWindow.document.close();
      }
    } catch (error) {
      console.error("Error printing order:", error);
      toast.error(t("Failed to print order"));
    }
  };

  if (!show || !orderDetails) return null;

  return (
    <>
      <RightDrawer
        open={show}
        onClose={onClose}
        width="559px"
        placement={isRTL ? "left" : "right"}
        dir={isRTL ? "rtl" : "ltr"}
        hideHeader
        fullBleed
        panelClassName="oh-right-drawer__panel--order"
      >
        <div className="oh-order-drawer">
          <div className="oh-order-drawer-header">
            <div className="oh-order-drawer-header__row">
              <h2 className="oh-order-drawer-header__title">
                {t("order", "Order")} #{getExternalOrderId(orderDetails)}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="oh-order-drawer-header__close"
                aria-label={t("close", "Close")}
              >
                <MDBIcon fas icon="times" style={{ fontSize: "16px" }} />
              </button>
            </div>
            <div className="oh-order-drawer-meta">
              <div className="oh-order-drawer-meta__item">
                <span className="oh-order-drawer-meta__label">
                  {t("status", "Status")}
                </span>
                {renderOrderStatus()}
              </div>
            </div>
          </div>

          <div className="oh-order-drawer-tabs" role="tablist">
            {DRAWER_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`oh-order-drawer-tab${activeTab === tab.id ? " is-active" : ""}`}
                onClick={() => handleTabChange(tab.id, tab.loads)}
              >
                {t(tab.labelKey)}
              </button>
            ))}
          </div>

          <div className="oh-order-drawer-content">
            {isTabLoading && (
              <div className="oh-order-drawer-content__loading">
                <div className="text-center">
                  <MDBIcon icon="circle-notch" spin size="2x" />
                  <p className="mt-2 mb-0">{t("loading", "Loading...")}</p>
                </div>
              </div>
            )}
            {activeTab === "details" && (
              <OrderDetailsTab
                orderDetails={orderDetails}
                formatDateTime={formatDateTime}
                getExternalOrderId={getExternalOrderId}
                t={t}
                isRTL={isRTL}
              />
            )}
            {activeTab === "timeline" && (
              <OrderTimeline
                show
                orderDetails={orderDetails}
                embedded
                onLoadingChange={setIsTabLoading}
              />
            )}
            {activeTab === "customer" && (
              <CustomerInfoTab
                orderDetails={orderDetails}
                formatDateTime={formatDateTime}
                t={t}
              />
            )}
          </div>

          <div className="oh-order-drawer-footer">
            <Button
              color="primary"
              size="xs"
              className="oh-order-drawer-print-btn"
              onClick={handlePrint}
              disabled={isProcessing}
              icon={<MDBIcon icon="print" />}
            >
              {t("print")}
            </Button>
          </div>
        </div>
      </RightDrawer>

      {showDeleteModal && orderToDelete && (
        <DeleteModal
          text={
            orderToDelete.actionType === "CANCELLED"
              ? t("cancelOrderConfirm", {
                  id: getExternalOrderId(orderToDelete),
                })
              : t("rejectOrderConfirm", {
                  id: getExternalOrderId(orderToDelete),
                })
          }
          showModal={showDeleteModal && orderToDelete !== null}
          confirmDelete={handleConfirmDelete}
          onClose={handleCloseDeleteModal}
          isLoading={isProcessing}
          confirmText={
            orderToDelete?.actionType === "CANCELLED" ? "Cancel Order" : "reject"
          }
          confirmIcon="fas fa-times"
        />
      )}
    </>
  );
};

export default OrderDetailsDrawer;